import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { cn } from 'lib/utils';
import { useAuthContext } from 'modules/auth/context';
import ProfileSettings from 'pages/admin/ProfileSettings';
import OrganizationSettings from 'pages/admin/OrganizationSettings';
import OrganizationPreferences from 'pages/admin/OrganizationPreferences';
import OrganizationNotifications from 'pages/admin/OrganizationNotifications';
import UserManagement from 'pages/admin/UserManagement';
import UserDetails from 'pages/admin/UserDetails';
import Permissions from 'pages/admin/Permissions';
import Transactions from 'pages/admin/Transactions';
import InviteManager from 'pages/InviteManager';
import { visibleGroups, type SettingsSection, type SettingsSectionEntry } from './registry';
import { useSettingsDialog } from './useSettingsDialog';
import { SettingsSectionHeader } from './SettingsSectionHeader';

/** Existing settings pages re-hosted in the dialog (each keeps its own data fetching + toasts). */
const SECTION_CONTENT: Record<SettingsSection, React.ComponentType> = {
  profile: ProfileSettings,
  org: OrganizationSettings,
  'org-preferences': OrganizationPreferences,
  'org-notifications': OrganizationNotifications,
  members: UserManagement,
  roles: Permissions,
  'access-keys': InviteManager,
  billing: Transactions,
};

/** A line under the section title, for pages that don't write their own. */
const SECTION_DESCRIPTION_KEYS: Partial<Record<SettingsSection, string>> = {
  billing: 'your_current_plan_feature_limits_and_resource_usage',
};

/** Phone pills put the everyday sections first; any other visible section follows in nav order. */
const PHONE_PILL_ORDER: SettingsSection[] = ['profile', 'org', 'members', 'roles', 'billing', 'access-keys'];

/** A shorter pill label where the nav's would crowd the phone strip. */
const PHONE_PILL_LABEL_KEYS: Partial<Record<SettingsSection, string>> = {
  roles: 'settings_nav_roles_short',
};

function phonePillOrder(entries: SettingsSectionEntry[]): SettingsSectionEntry[] {
  const rank = (slug: SettingsSection) => {
    const index = PHONE_PILL_ORDER.indexOf(slug);
    return index === -1 ? PHONE_PILL_ORDER.length : index;
  };
  return [...entries].sort((a, b) => rank(a.slug) - rank(b.slug));
}

interface SectionLinkProps {
  entry: SettingsSectionEntry;
  active: boolean;
  onSelect: () => void;
}

/** Desktop nav row: the current section is an ink fill with white text and icon. */
function NavRow({ entry, active, onSelect }: SectionLinkProps) {
  const { t } = useTranslation();
  const Icon = entry.icon;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex h-[34px] w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-[13.5px] transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        active ? 'bg-foreground font-semibold text-background' : 'font-medium text-foreground/75 hover:bg-muted hover:text-foreground',
      )}
    >
      <Icon className={cn('h-[15px] w-[15px] shrink-0', active ? 'text-background' : 'text-muted-foreground')} aria-hidden />
      <span className="min-w-0 flex-1 truncate">{t(entry.labelKey)}</span>
    </button>
  );
}

/** Phone pill: 34px drawn, 44px to tap. The current one scrolls itself into view. */
function NavPill({ entry, active, onSelect }: SectionLinkProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Opening straight onto a later section (e.g. billing) would otherwise leave
    // its pill off-screen. Hidden (desktop) pills don't scroll anything.
    if (active) ref.current?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [active]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onSelect}
      aria-current={active ? 'page' : undefined}
      className="-my-[5px] shrink-0 rounded-full py-[5px] focus-visible:outline-none [&:focus-visible>span]:ring-2 [&:focus-visible>span]:ring-ring [&:focus-visible>span]:ring-offset-2"
    >
      <span
        className={cn(
          'inline-flex h-[34px] items-center whitespace-nowrap rounded-full px-3 text-[13.5px] transition-colors',
          active ? 'bg-foreground font-semibold text-background' : 'border border-border bg-card text-foreground/80',
        )}
      >
        {t(PHONE_PILL_LABEL_KEYS[entry.slug] ?? entry.labelKey)}
      </span>
    </button>
  );
}

/** Desktop only — the phone shell signs out from the More sheet's account row. */
function SignOutButton({ onSignOut }: { onSignOut: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onSignOut}
      className="flex h-[34px] w-full items-center gap-2.5 rounded-lg px-2.5 text-[13.5px] font-medium text-error-600 transition-colors hover:bg-error-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-error-400 dark:hover:bg-error-900/30"
    >
      <LogOut className="h-[15px] w-[15px] shrink-0" aria-hidden />
      {t('usermenu_signout')}
    </button>
  );
}

/** URL-driven via `?settings=<section>` (see useSettingsDialog). Mounted once in the Main layout. */
export function SettingsDialog() {
  const { t } = useTranslation();
  const { isOpen, section, memberId, close, setSection, setMember } = useSettingsDialog();
  const {
    state: { permissions },
    methods,
  } = useAuthContext();

  // Strip `?settings=` before clearing auth so the dialog isn't left mounted
  // over the login redirect RequireAuth performs once isAuthenticated flips.
  const handleSignOut = () => {
    close();
    methods.logout();
  };

  const groups = visibleGroups(permissions);
  const flatItems = groups.flatMap((g) => g.items);
  const activeEntry = flatItems.find((item) => item.slug === section) ?? flatItems[0];
  const activeSlug = activeEntry?.slug ?? 'profile';

  let content: React.ReactNode;
  if (activeSlug === 'members') {
    content = memberId ? (
      <UserDetails memberId={memberId} onBack={() => setMember(null)} />
    ) : (
      <UserManagement onSelectMember={setMember} />
    );
  } else {
    const Content = SECTION_CONTENT[activeSlug];
    content = <Content />;
  }

  // Profile and the members list write their own heading (the list's carries
  // its count and the invite button); every other section gets the dialog's.
  const ownsHeading = activeSlug === 'profile' || (activeSlug === 'members' && !memberId);
  const descriptionKey = SECTION_DESCRIPTION_KEYS[activeSlug];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent
        aria-describedby={undefined}
        className="flex h-[100dvh] max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 md:h-[min(88vh,720px)] md:w-[calc(100vw-1.5rem)] md:max-w-[960px] md:flex-row md:rounded-lg md:border"
        // Phones: a ghost X in the 56px header, 44px to tap. Desktop: a quiet
        // filled circle in the corner — the dialog has no padded header of its own.
        closeClassName="z-10 right-3 top-1.5 flex h-11 w-11 items-center justify-center text-foreground/80 opacity-100 transition-colors hover:bg-muted hover:text-foreground md:right-3.5 md:top-3.5 md:h-8 md:w-8 md:rounded-full md:bg-muted"
      >
        {/* Single a11y title — the visible headings below are plain elements
            so the Radix labelling id exists exactly once. */}
        <DialogTitle className="sr-only">{t('settings.title')}</DialogTitle>

        {/* Desktop nav column */}
        <aside className="hidden w-56 shrink-0 flex-col overflow-y-auto border-r border-border bg-background px-3 py-4 md:flex">
          <p className="px-2.5 pb-2 pt-1 text-sm font-bold leading-[17px] text-foreground">{t('settings.title')}</p>
          <nav aria-label={t('settings.title')} className="flex flex-col gap-0.5">
            {groups.map((group) => (
              <div key={group.labelKey} className="flex flex-col gap-0.5">
                <p className="px-2.5 pb-1 pt-3.5 text-[10px] font-semibold uppercase leading-3 tracking-wider text-muted-foreground/70">
                  {t(group.labelKey)}
                </p>
                {group.items.map((item) => (
                  <NavRow
                    key={item.slug}
                    entry={item}
                    active={item.slug === activeSlug}
                    onSelect={() => setSection(item.slug)}
                  />
                ))}
              </div>
            ))}
          </nav>
          <div className="mt-auto shrink-0 pt-2">
            <SignOutButton onSignOut={handleSignOut} />
          </div>
        </aside>

        {/* Phone header: title + a strip of section pills that fades at the right edge */}
        <div className="shrink-0 md:hidden">
          <div className="flex h-14 items-center border-b border-border pl-4 pr-14">
            <p className="truncate text-[17px] font-bold tracking-[-0.01em] text-foreground">{t('settings.title')}</p>
          </div>
          <nav
            aria-label={t('settings.title')}
            className="no-scrollbar flex gap-2 overflow-x-auto border-b border-border px-4 py-3 [-webkit-mask-image:linear-gradient(90deg,#000_88%,transparent)] [mask-image:linear-gradient(90deg,#000_88%,transparent)]"
          >
            {phonePillOrder(flatItems).map((item) => (
              <NavPill
                key={item.slug}
                entry={item}
                active={item.slug === activeSlug}
                onSelect={() => setSection(item.slug)}
              />
            ))}
          </nav>
        </div>

        {/* Content pane */}
        <div className="min-w-0 flex-1 overflow-y-auto scrollbar-thin">
          <div className="p-4 md:px-7 md:py-6">
            {!ownsHeading && activeEntry && (
              <SettingsSectionHeader
                title={t(activeEntry.labelKey)}
                description={descriptionKey ? t(descriptionKey) : undefined}
                className="mb-4"
              />
            )}
            {content}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SettingsDialog;
